// src/App.jsx
import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { publicRoutes, privateRoutes } from "./routes/appRoutes";
import { useAuth } from "./contexts/AuthContext";
import { temArea, AREAS, pode } from "./utils/permissoes";

function PerfilIncompleto() {
  const { usuario, logout } = useAuth();

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        style={{
          maxWidth: 560,
          width: "100%",
          borderRadius: 14,
          border: "1px solid rgba(0,0,0,0.12)",
          padding: 20,
        }}
      >
        <h2 style={{ marginTop: 0 }}>Perfil incompleto</h2>
        <p style={{ marginBottom: 10 }}>
          Seu usuário está logado, mas não possui <b>grupo</b> vinculado no cadastro.
        </p>
        <p style={{ marginTop: 0, marginBottom: 16 }}>
          Peça para o Gestor/Administrador completar seu cadastro em <b>Cadastros → Usuários</b>.
        </p>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <button
            onClick={logout}
            style={{
              padding: "10px 14px",
              borderRadius: 10,
              border: "1px solid rgba(0,0,0,0.18)",
              background: "transparent",
              cursor: "pointer",
            }}
          >
            Sair
          </button>
        </div>
        <div style={{ marginTop: 12, fontSize: 12, opacity: 0.75 }}>
          Usuário: {usuario?.email || usuario?.uid || "-"}
        </div>
      </div>
    </div>
  );
}

function PrivateRoute({ children }) {
  const { usuario, carregando } = useAuth();

  if (carregando) return <div>Carregando...</div>;
  if (!usuario) return <Navigate to="/login" replace />;

  // Falha segura: nunca deixar o app seguir sem grupoId
  if (!usuario.grupoId) return <PerfilIncompleto />;

  return children;
}

function inferirAreaDaRota(path) {
  if (!path) return null;

  // Rotas globais (sempre acessíveis, independente do módulo)
  const globais = ["/gerenciar/mudar-cliente", "/gerenciar/mudar-empresa", "/gerenciar/conta", "/perfil", "/login"];
  if (path === "/") return null;
  if (globais.includes(path)) return null;

  if (String(path).startsWith("/financeiro")) return AREAS.FINANCEIRO;

  // Por padrão, tudo que não for Financeiro é Manutenção
  return AREAS.MANUTENCAO;
}

function AreaRoute({ area, children }) {
  const { usuario, carregando } = useAuth();
  if (carregando) return <div>Carregando...</div>;
  if (!usuario) return <Navigate to="/login" replace />;
  if (!usuario.grupoId) return <PerfilIncompleto />;

  if (area && !temArea(usuario, area)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function inferirPermissaoDaRota(path) {
  // Convenção:
  // /cadastros/<recurso>            -> tabela
  // /cadastros/<recurso>/novo       -> form
  // /cadastros/<recurso>/editar/:id -> altera
  if (!path || typeof path !== "string") return null;
  const partes = path.split("/").filter(Boolean);
  if (partes.length < 2) return null;

  const [modulo, recurso, acaoRaw] = partes;
  if (modulo !== "cadastros") return null;
  if (!recurso) return null;

  let acao = "tabela";
  if (acaoRaw === "novo") acao = "form";
  if (acaoRaw === "editar") acao = "altera";

  return { modulo, recurso, acao };
}

function PermissaoRoute({ perm, children }) {
  const { usuario } = useAuth();
  if (!perm) return children;

  const ok = pode(usuario, perm.modulo, perm.recurso, perm.acao);
  if (ok) return children;

  // ✅ Sem aviso (evita "tela de acesso restrito"). Quem não tem permissão não navega.
  return <Navigate to="/" replace />;
}

export default function App() {
  return (
    <Routes>
      {/* Rotas públicas */}
      {publicRoutes.map((route) => (
        <Route key={route.path} path={route.path} element={route.element} />
      ))}

      {/* Rotas privadas */}
      {privateRoutes.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={
            <PrivateRoute>
              {(() => {
                const areaEfetiva = route.area || inferirAreaDaRota(route.path);
                const permEfetiva = route.perm || inferirPermissaoDaRota(route.path);

                const baseElement = route.element;
                const elementComPermissao = permEfetiva ? (
                  <PermissaoRoute perm={permEfetiva}>{baseElement}</PermissaoRoute>
                ) : (
                  baseElement
                );

                return areaEfetiva ? (
                  <AreaRoute area={areaEfetiva}>{elementComPermissao}</AreaRoute>
                ) : (
                  elementComPermissao
                );
              })()}
            </PrivateRoute>
          }
        />
      ))}

      {/* Qualquer rota desconhecida manda pra "/" */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
